<template>
    <div>
        <b-table id="pap-queue-table" ref="pap-queue-table" :api-url="apiUrl" sort-icon-left show-empty
            empty-text="Queue is Empty" empty-filtered-text="Queue is Empty" :tbody-tr-class="rowClass"
            :fields="tableFields" :items="items" :current-page="pageNumber" :per-page="pageSize">
            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>

            <template #cell(patient)="data">
                <router-link :to="{ name: 'PatientDashboard', params: { id: data.item.patientId.toString() } }"
                    title="View Patient">
                    <strong>{{ data.value.lastName }}, {{ data.value.firstName }}</strong>
                </router-link>
            </template>
            <template #cell(edit)="data">
                <b-button size="sm" @click="editClick(data.item)" title="Review">
                    <b-icon icon="clipboard-data" />
                </b-button>
            </template>
            <template #cell(removal)="data">
                <b-button size="sm" @click="removalClick(data.item)" variant="danger" :disabled="canRemove(data.item)"
                    title="Remove">
                    <b-icon icon="x-circle" />
                </b-button>
            </template>
            <template #cell(status)="data">
                <b-badge :class="getStatusBadgeClass(data.item.status)">
                    {{ data.value }}
                    <span v-if="data.value === 'Denied' && data.item.rejectReason" class="badge-denied-reason">
                        - {{ data.item.rejectReason }}
                    </span>
                </b-badge>
            </template>
            <template #cell(created)="data">
                <span v-b-tooltip.hover :title='`Created by ${data.item.createdBy}`'>
                    {{ relativeFormat(data.value) }}
                </span>
            </template>
            <template #cell(updated)="data">
                <span v-b-tooltip.hover :title='`Updated by ${data.item.updatedBy}`'>
                    {{ relativeFormat(data.value) }}
                </span>
            </template>
        </b-table>

        <BottomPagerBar v-model="pageNumber" :total-rows="items.length" :per-page="pageSize" show-page-size-selector
            show-total :loading="loading" @change-page-size="pageSize = $event" />

        <b-modal id="pap-queue-modal" size="xl" title="PAP Form Review" v-model="showModal" centered scrollable no-fade>
            <template v-if="modalItem">
                <b-row>
                    <b-col cols="12" lg="6">
                        <SearchComponent v-model="selectedProgramForPap" label="Program" search-u-r-l="/Program"
                            :params="{ active: true }" clearable prepopulate />
                    </b-col>
                    <b-col cols="12" lg="6">
                        <EnumSelector v-model="modalItem.status" label="Status:"
                            :enum-codes="PatientAssistanceProgramStatus" />

                        <b-form-group
                            v-if="rejectReasons?.length && modalItem.status === PatientAssistanceProgramStatus.Denied"
                            label="Denied Reason:">
                            <b-select v-model="modalItem.rejectReason" :options="rejectReasons" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" lg="3">
                        <b-form-group label="Expiration Date:">
                            <el-date-picker v-model="modalItem.expirationDate" type="date" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" lg="3">
                        <b-form-group label="Authorized Fills:">
                            <b-input v-model="modalItem.authorizedFills" type="number" min="0"
                                @blur="modalItem.authorizedFills = !modalItem.authorizedFills ? modalItem.authorizedFills : Math.max(modalItem.authorizedFills, 0)" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" lg="6">
                        <b-form-group label="Drug:">
                            <SearchComponent :id="selectedDrugForPap?.packageID" v-model="selectedDrugForPap"
                                label="Drug" search-u-r-l="/drug" :params="{ onlyActive: false, allSources: true }"
                                :hide-label="true" clearable
                                :params-in-get-by-id="{ drugSource: selectedDrugForPap?.source }" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <NotesList ref="notesList" :paging="false" :entity-id="modalItem.id"
                            :entity-type="EntityType.PAP" />
                        <NotesForm v-if="modalItem.id" :entity-id="modalItem.id" :entity-type="EntityType.PAP"
                            @saved="noteSaved" />
                    </b-col>
                </b-row>

                <br>

                <div v-if="modalItem.imageId">
                    <b-embed :src="imageSrc" type="embed" />
                </div>
                <div v-if="!!modalItem.attachmentIds.length">
                    <div v-for="(source, i) in attachmentSources" :key="i">
                        <b-embed :src="source" type="embed" />
                    </div>
                </div>
            </template>

            <template #modal-footer="{ hide }">
                <div>
                    <b-button size="sm" variant="secondary" @click="hide">Cancel</b-button>
                </div>
                <div class="d-flex" style="gap: 6px;">
                    <b-button size="sm" variant="danger" @click="reject">
                        Reject
                        <i class="fa fa-times fa-fw" />
                    </b-button>
                    <b-button size="sm" variant="success" @click="approve">
                        Approve
                        <i class="fa fa-check fa-fw" />
                    </b-button>
                </div>
                <div>
                    <b-button size="sm" variant="primary" @click="save">Save</b-button>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { PatientAssistanceProgram, PatientAssistanceProgramStatus } from "@/models/PatientAssistanceProgram";
import moment from "moment/moment";
import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import { BTable, BvModalEvent, BvTableCtxObject } from "bootstrap-vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Program } from "@/models/Program";
import SearchComponent from "@/components/SearchComponent.vue";
import EnumSelector from "../EnumSelector.vue";
import { DatePicker } from 'element-ui';
import { toRaw } from "vue";
import { Drug, DrugProgram } from "@/models/Drug/Drug";
import { DrugSource } from "@/models/Drug/DrugProduct";
import NotesList from "../Note/NotesList.vue";
import NotesForm from "../Note/NotesForm.vue";
import { EntityType } from "@/models/EntityType";
import BottomPagerBar from "../BottomPagerBar.vue";
import { addBusinessDays } from "@/util/DateUtils";
import TextUtils from "@/util/TextUtils";

@Component({
    name: "PatientAssistanceProgramTable",
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        NotesList,
        NotesForm,
        BottomPagerBar
    }
})
export default class PatientAssistanceProgramTable extends Vue {
    @Prop() tableFilter!: AxiosRequestConfig;
    @Prop() apiUrl!: string;
    @Prop({ type: Boolean, default: false }) allowRemoval!: boolean;
    protected modalItem: PatientAssistanceProgram | null = null;
    protected imageSrc: string = "";
    protected showModal: boolean = false;
    protected attachmentSources: string[] = [];
    protected selectedProgramForPap = new Program();
    protected selectedDrugForPap = new Drug();

    protected pageNumber = 1;
    protected pageSize = 25;
    protected loading = false;

    PatientAssistanceProgramStatus = PatientAssistanceProgramStatus;
    EntityType = EntityType;

    @Ref('pap-queue-table') readonly table!: BTable;

    mounted() {
        this.getRejectReasons();
        this.getNextTouchDaysFromConfig();
        this.fetchItems();
    }

    clearAndRefresh() {
        this.modalItem = null;
        this.attachmentSources = [];
        this.fetchItems();
    }

    async save(e: BvModalEvent) {
        e.preventDefault();
        this.saveApplication();
    }

    async approve(e: BvModalEvent) {
        e.preventDefault();
        this.modalItem!.status = PatientAssistanceProgramStatus.Approved;
        this.saveApplication();
    }

    async reject(e: BvModalEvent) {
        e.preventDefault();
        this.modalItem!.status = PatientAssistanceProgramStatus.Denied;
        this.saveApplication();
    }

    async saveApplication() {
        if (this.rejectReasons.length &&
            this.modalItem!.status === PatientAssistanceProgramStatus.Denied &&
            !this.modalItem!.rejectReason) {
            this.$bvModal.msgBoxOk(`You must select a denied reason.`);
            return;
        }

        const url = `PatientAssistanceProgram`;
        try {
            await Axios.post(url, {
                ...this.modalItem,
                program: this.selectedProgramForPap,
                drug: this.selectedDrugForPap ? {
                    drugSource: this.selectedDrugForPap.source,
                    packageId: this.selectedDrugForPap.drugId
                } : null
            });
            this.showModal = false;
            this.clearAndRefresh();
        } catch (error) {
            const err = error as AxiosError;
            if (err.message === "Request failed with status code 403") {
                err.message = "You are not authorized to save PAP";
            }
            this.$notification(NotificationOptions.error(err));
        }
    }

    canRemove(item: PatientAssistanceProgram) {
        if (item.status != PatientAssistanceProgramStatus.Created) return true;
        if (item.attachmentIds.length) return true;
        return false;
    }

    async removalClick(item: PatientAssistanceProgram) {
        try {
            await Axios.post(`api/PatientAssistanceProgram/Remove/${item.id}`);
            this.clearAndRefresh();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get selectedHasDifferentProgram() {
        return (this.selectedProgramForPap?.id ?? 0) !== (this.modalItem?.program?.id ?? 0);
    }

    async editClick(item: PatientAssistanceProgram) {
        // Revoke old blob URLs to free memory and prevent black screen issues
        if (this.imageSrc) {
            URL.revokeObjectURL(this.imageSrc);
            this.imageSrc = "";
        }
        this.attachmentSources.forEach(src => URL.revokeObjectURL(src));
        this.attachmentSources = [];

        this.modalItem = structuredClone(toRaw(item));
        this.selectedProgramForPap = Object.assign(new Program(), item.program);
        this.selectedDrugForPap = Object.assign(new Drug(), {
            source: item.drug?.drugSource,
            drugId: item.drug?.packageID
        });

        try {
            await this.fetchAttachmentSources();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        if (item.imageId) {
            const url = `Image/${item.imageId}`;
            const config: AxiosRequestConfig = { responseType: "blob" };

            try {
                const responseImage = await Axios.get(url, config);
                // PAP attachments are always PDFs - backend converts non-PDF images automatically
                const blob = new Blob([responseImage.data], { type: 'application/pdf' });
                this.imageSrc = URL.createObjectURL(blob);
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            this.imageSrc = "";
        }

        this.showModal = true;
    }

    async fetchAttachmentSources() {
        if (!this.modalItem) return;

        const config: AxiosRequestConfig = { responseType: "blob" };
        for (const attachmentId of this.modalItem.attachmentIds) {
            const url = `Image/${attachmentId}`;
            try {
                const img = await Axios.get(url, config);
                const blob = new Blob([img.data], { type: 'application/pdf' });
                this.attachmentSources.push(URL.createObjectURL(blob));
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    get tableFields() {
        const fields = [
            { key: "id", sortable: true },
            { key: "patient", hidden: !!this.tableFilter?.params?.id },
            { key: "program.name", label: "Program" },
            { key: "drug.drugName", label: "Drug" },
            { key: "status", formatter: (val: number) => TextUtils.camelCaseToNormal(PatientAssistanceProgramStatus[val].toString()) },
            { key: "created", sortable: true },
            { key: "updated", label: "Last Activity", sortable: true },
            { key: "nextTouchDate", label: "Next Touch", sortable: true, formatter: this.formatNextTouch },
            { key: "edit", label: "" },
            { key: "removal", label: "", hidden: !this.allowRemoval }
        ];
        return fields.filter(f => f.hidden !== true);
    }

    protected items: PatientAssistanceProgram[] = [];

    @Watch("apiUrl")
    @Watch("tableFilter")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("apiUrl")
    @Watch("tableFilter")
    async fetchItems() {
        try {
            this.loading = true;
            const resp = await Axios.get<PatientAssistanceProgram[]>(this.apiUrl, this.tableFilter);
            this.items = resp.data.filter(a => !isNaN(a.imageId)).map(a => new PatientAssistanceProgram(a));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            return [];
        } finally {
            this.loading = false;
        }
    }

    rowClass(item: any, type: any) {
        if (!item || type !== "row") return;

        if (item.status === PatientAssistanceProgramStatus.Denied) return 'table-danger';
        if (item.status === PatientAssistanceProgramStatus.Approved) return 'table-success';
        if (item.status === PatientAssistanceProgramStatus.Deleted) return 'pap-deleted-row';
    }

    getStatusBadgeClass(status: PatientAssistanceProgramStatus): string {
        switch (status) {
            case PatientAssistanceProgramStatus.Denied:
                return 'badge-danger';
            case PatientAssistanceProgramStatus.Approved:
                return 'badge-success';
            case PatientAssistanceProgramStatus.Deleted:
                return 'badge-deleted';
            default:
                return 'badge-primary';  // Primary badge for other statuses
        }
    }

    protected rejectReasons: string[] = [];
    protected storeBusinessHours: Map<number, any> = new Map();
    protected nextTouchDays: any[] = [];
    protected programNextTouchDays: Program[] = [];

    async getRejectReasons() {
        try {
            const response = await this.$http.get<string[]>("/Configuration/PatientAssistanceProgramRejectReasons");
            this.rejectReasons = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getNextTouchDaysFromConfig() {
        try {
            const resp = await this.$http.get<any>("/Configuration");
            if (resp.data) {
                this.nextTouchDays = resp.data.papNextTouchDays || [];
            }

            const programsResp = await this.$http.get<Program[]>("/Program");
            if (programsResp.data) {
                this.programNextTouchDays = programsResp.data.filter(p => p.papNextTouchDays && p.papNextTouchDays.length > 0);
            }

            const storesResp = await this.$http.get<any>("/Store/BusinessHours");
            if (storesResp.data) {
                storesResp.data.forEach((store: any) => {
                    if (store.businessHours) {
                        this.storeBusinessHours.set(store.id, store.businessHours);
                    }
                });
            }
        } catch (err) {
            console.error(err);
        }
    }

    findNextTouchMatch(nextTouchDays: any[], status: PatientAssistanceProgramStatus): any | undefined {
        return nextTouchDays.find(i => i.status === status) ||
            nextTouchDays.find(i => i.status == null);
    }

    formatNextTouch(d: Date, _: string, item: PatientAssistanceProgram): string {
        const inputMoment = moment.utc(d);
        if (!d || !inputMoment.isValid()) {
            return moment().add(1, 'day').format("L LT");
        }

        const { status } = item;
        const programId = item.program?.id;
        const storeId = item.storeId;
        const programMatch = this.programNextTouchDays.find(p => p.id === programId);

        // if there is a program configuration and nextTouch return it; otherwise, fall back on the global next touch; else add 1 day
        const programNextTouchMatch = programMatch?.papNextTouchDays ? this.findNextTouchMatch(programMatch.papNextTouchDays, status) : undefined;
        const bestMatch = programNextTouchMatch ? programNextTouchMatch : this.findNextTouchMatch(this.nextTouchDays, status);
        const daysToAdd = bestMatch?.days ?? 1;

        const startDate = inputMoment.local();

        // Get store-specific business hours if storeId is available
        const businessHours = storeId ? this.storeBusinessHours.get(storeId) : undefined;

        return addBusinessDays(startDate, daysToAdd, businessHours).format("L LT");
    }

    noteSaved() {
        this.loadNotes();
    }

    loadNotes() {
        if (this.$refs.notesList instanceof NotesList)
            this.$refs.notesList.loadNotes();
    }

    relativeFormat(date: Date) {
        return moment(date).fromNow();
    }
}
</script>

<style scoped>
::v-deep(.embed-responsive) {
    height: calc(100vh - 200px);
}

@media (min-width: 992px) {
    ::v-deep(.embed-responsive) {
        height: calc(100vh - 370px);
    }
}

/* Custom row styling for deleted PAP records */
::v-deep(.pap-deleted-row) {
    background-color: #d3d3d3 !important;
    color: #5a5a5a !important;
}

::v-deep(.pap-deleted-row:hover) {
    background-color: #c0c0c0 !important;
}

/* Custom badge styling for deleted status */
::v-deep(.badge-deleted) {
    background-color: #5a5a5a;
    color: #ffffff;
}

/* Denied reason styling - lighter red extension of badge */
::v-deep(.badge-denied-reason) {
    opacity: 0.7;
    font-size: 0.85em;
    font-weight: 500;
}
</style>
